import { RepeatIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Box, Button, chakra, Spinner } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { Column, Row, useSortBy, useTable } from 'react-table';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelIndicator';
import { DownloadIcon } from '~components/ui/icons/DownloadIcon';
import { ZteRowFragment } from './__generated__/ZteTable.graphql';

export enum Columns {
  BatchName = 'Batch name',
  CsvFileName = 'Csv file name',
  AllCsvRows = 'All csv rows',
  Validated = 'Validated',
  Organization = 'Organization',
  Customer = 'Customer',
  UnpairedDevices = 'Unpaired devices',
  DevicePaired = 'Devices Paired',
  DevicesClaimed = 'Devices claimed',
  ClaimingError = 'Claiming error',
  DateCreated = 'Date created',
  User = 'User',
  Status = 'Status',
}

type UseZteTableProps = {
  data: ZteRowFragment[];
  onRefreshRow?: (id: string) => void;
  onBatchNameClick: (row: ZteRowFragment) => void;
  refreshingRows?: Set<string>;
};

export const useZteTable = ({
  data,
  onBatchNameClick,
  onRefreshRow,
  refreshingRows,
}: UseZteTableProps) => {
  const columns = useMemo<Array<Column<ZteRowFragment>>>(() => {
    const DateCell = ({ value }: { value: string }) => (
      <>{value ? format(new Date(value), 'MMM d, y') : 'Never'}</>
    );

    const NoWrapCell = ({ value }: { value: string }) => (
      <chakra.span whiteSpace="nowrap">{value}</chakra.span>
    );
    const batchNameCell = ({ row }: { row: Row<ZteRowFragment> }) => (
      <chakra.span
        whiteSpace="nowrap"
        cursor="pointer"
        textDecoration="underline"
        onClick={() => onBatchNameClick(row.original)}
      >
        {row.original.batchName}
      </chakra.span>
    );

    const WarningCell = ({ row }: { row: Row<ZteRowFragment> }) => {
      const { status } = row.original;

      if (status === 'ERROR') {
        return <WarningTwoIcon color="orange.500" />;
      }

      if (status === 'PENDING') {
        return <FormLabelPendingIndicator isPending={true} variant="default" />;
      }

      return null;
    };

    const StatusCell = ({ row }: { row: { original: ZteRowFragment } }) => {
      const { status, downloadUrl, id } = row.original;
      const isRefreshing = refreshingRows?.has(id) ?? false;

      if (status === 'UPLOADED' && downloadUrl) {
        return (
          <chakra.span whiteSpace="nowrap">
            <Box as="a" href={downloadUrl} download cursor={'pointer'} opacity={1}>
              <DownloadIcon width="5" height="5" />
            </Box>
          </chakra.span>
        );
      }

      if (status === 'PENDING') {
        return (
          <chakra.span whiteSpace="nowrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRefreshRow?.(id)}
              isLoading={isRefreshing}
              p={0}
              minW="auto"
              height="24px"
            >
              <RepeatIcon boxSize={4} color="blue.700" />
            </Button>
          </chakra.span>
        );
      }

      if (status === 'ERROR' && downloadUrl) {
        return (
          <chakra.span whiteSpace="nowrap">
            <Box as="a" href={downloadUrl} download cursor={'pointer'} opacity={1}>
              <DownloadIcon width="5" height="5" />
            </Box>
          </chakra.span>
        );
      }

      return (
        <chakra.span whiteSpace="nowrap">
          <Spinner size="sm" />
        </chakra.span>
      );
    };

    const UserCell = ({ value }: { value: string }) => (
      <chakra.span whiteSpace="nowrap">{value}</chakra.span>
    );

    return [
      {
        id: Columns.BatchName,
        Header: 'Batch name',
        accessor: (o) => o.batchName,
        Cell: batchNameCell,
      },
      {
        id: 'warning',
        Header: '',
        accessor: () => '',
        Cell: WarningCell,
      },
      {
        id: Columns.CsvFileName,
        Header: 'Csv file Name',
        accessor: (o) => o.fileName,
        Cell: NoWrapCell,
      },
      {
        id: Columns.AllCsvRows,
        Header: 'All csv rows',
        accessor: (o) => o.totalDisplay,
        Cell: NoWrapCell,
      },
      {
        id: Columns.Validated,
        Header: 'Validated',
        accessor: (o) => o.validated,
        Cell: NoWrapCell,
      },
      {
        id: Columns.Organization,
        Header: 'Organization',
        accessor: (o) => o.organizationName,
        Cell: NoWrapCell,
      },
      {
        id: Columns.Customer,
        Header: 'Customer',
        accessor: (o) => o.customerName,
        Cell: NoWrapCell,
      },
      {
        id: Columns.DevicePaired,
        Header: 'Devices Paired',
        accessor: (o) => o.pairedDisplay,
        Cell: NoWrapCell,
      },
      {
        id: Columns.DevicesClaimed,
        Header: 'Devices claimed',
        accessor: (o) => o.claimedDisplay,
        Cell: NoWrapCell,
      },
      {
        id: Columns.User,
        Header: Columns.User,
        accessor: (o) => o.createdBy,
        Cell: UserCell,
      },
      {
        id: Columns.DateCreated,
        Header: 'Date created',
        accessor: (o) => o.createdAt,
        Cell: DateCell,
      },
      {
        id: Columns.Status,
        Header: '',
        accessor: (o) => o.status,
        Cell: StatusCell,
      },
    ];
  }, [onBatchNameClick, onRefreshRow, refreshingRows]);

  return useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: Columns.DateCreated, desc: true }],
      },
    },
    useSortBy,
  );
};
